import React, { useEffect } from 'react'
import {
  DcButton,
  DcDiv,
  DcDropDown,
  DcFlexBox,
  DcInput,
  DcSwitch,
  DcText,
  PixelFactoryContext,
  SectionContent,
} from 'pixel-base'
import ClickOutside from 'rechat-react-click-outside'
import styled from 'styled-components'

import { updateCustomerAddress } from 'utils/api-routes.util'

import { IPixelFactory } from '../../../../../../decl.d'
import SelectCity from '../../../components/SelectCity'

interface Address {
  first_name: string
  last_name: string
  customer_reference: string
  phone: string
  email: string
  address1: string
  city: string
  country: string
  country_code: string
  state: string
  state_code: string
  postcode: string
}

interface City {
  name: string
}

interface Country {
  two_letter_abbreviation: string
  abbreviation: string
  name: string
  country_code: string
}

interface CountriesData {
  list: Country[]
  default: string
}
interface LineItem {
  order_id: number | null
  quantity: number
  product_id: number | string
  variation_id: number | string
  price: number | string
  total: number | string
  sku: string
  image: string | null
  weight: number | null
  description: string | null
  line_item_id: number | null
  name: string
  is_partial_return: boolean | null
  has_properties: boolean | null
  fulfillment_order_id: number | null
  fulfillment_order_line_item_id: number | null
}

interface OrderMeta {
  added_by: string | null
  sale_by_id: string | null
  sale_by: string | null
  order_source: string | null
  last_modified?: string
  modified_by?: string
  business_name?: string | null
}

interface CourierInfo {
  courier_name: string
  tracking_id: string
}

interface CourierTransaction {
  tracking_id: string
  weight: string
  courier_id: string
  cod_amount: string
  delivery_status: string
  created_at: string
  courier_name: string
  dispatch_time: string | null
  courier_reference: string | null
  dispatch_status: string | null
}

interface OrderHistory {
  order_id: number
  updated_value: string
  change_type: string
  user_name: string
  update_time: string
  is_bulk: boolean
  message: string
}
interface OrderDetails {
  is_billing_address_same: boolean
  customer_id: number
  is_editable: boolean
  shipping: Address
  billing: Address
  total: number
  weight: number
  order_discount: number
  complaint_count: number
  source_order_id: number | string
  split_source_order_id: string
  store_order_id: number
  store_id: number
  store_name: string
  status: string
  tags: string
  number: number
  created_via: string
  last_comment: string
  date_created: string
  is_refuser: boolean
  is_returning: boolean
  is_duplicate: boolean
  dispatch_id: number
  is_split_child: boolean
  is_split_main: boolean
  split_child_ids: string
  split_main_id: string
  exchanged_from_order_id: string
  print_customer_note_on_shipping_label: boolean
  print_customer_note_on_invoice: boolean
  meta: OrderMeta
  courier: CourierInfo
  payments: any[] // Define Payment interface if needed
  order_notes: any[]
  order_tags: any[]
  courier_transactions: CourierTransaction[]
  line_items: LineItem[]
  order_history: OrderHistory[]
  discount_lines: any[]
  order_shipping: any[]
  order_tax: any[]
  daraz_payment_transactions: any[]
}

interface IProps {
  orderId?: number
  customerId?: number
  cities: City[]
  countries: CountriesData
  OrderAddresses: {
    shipping_address: Address
    billing_address: Address
    is_billing_address_same: boolean
  }
  isSameAddress: boolean
  details: OrderDetails
  newOrder: boolean
  newOrderData: Partial<OrderDetails>
  handleClose: (data: OrderDetails) => void
  billingAddress: Address
  setNewOrderData: (data: Partial<OrderDetails>) => void
  allowcountries: Country[]
  setIsCustomerAdd: (data: boolean) => void
  setDetails: (data: OrderDetails) => void
}
const EditCustomerInfo = (props: IProps) => {
  const { showPageLoader, hidePageLoader, showSuccess, showFailure } =
    React.useContext<IPixelFactory>(PixelFactoryContext)
  const {
    OrderAddresses,
    countries,
    orderId,
    customerId,
    handleClose,
    billingAddress,
    setNewOrderData,
    newOrderData,
    newOrder,
    allowcountries,
    cities,
    setIsCustomerAdd,
    setDetails,
    details,
  } = props

  const [error, setError] = React.useState({
    error: '',
    field: '',
  })
  const [isSameAddress, setIsSameAddress] = React.useState<boolean>(
    newOrder
      ? newOrderData?.is_billing_address_same
      : OrderAddresses.is_billing_address_same,
  )
  const [shipping, setShipping] = React.useState({
    address_id: null,
    address1: null,
    city: '',
    country_name: null,
    country_code: null,
    email: null,
    first_name: null,
    last_name: null,
    phone: null,
    postcode: null,
    state: null,
    state_name: null,
    state_code: null,
    shipping_address_id: null,
  })
  const [billing, setBilling] = React.useState({
    address_id: null,
    address1: null,
    city: '',
    country_name: null,
    country_code: null,
    email: null,
    first_name: null,
    last_name: null,
    phone: null,
    postcode: null,
    state: null,
    state_name: null,
    state_code: null,
    billing_address_id: null,
  })
  const handleSubmit = () => {
    if (newOrder) {
      AddNewAddress()
    } else {
      handleUpdateAddress(orderId)
    }
  }

  const updatedData = () => {
    return {
      shipping_address: shipping,
      billing_address: billing,
      is_billing_address_same: isSameAddress,
    }
  }

  const handleUpdateAddress = (orderId) => {
    showPageLoader()
    updateCustomerAddress(orderId, customerId, updatedData()).subscribe(
      (res: any) => {
        hidePageLoader()
        if (res.response.data) {
          showSuccess('Address updated successfully')
          handleClose(res.response.data)
        } else {
          if (res.response.error && res.response.field) {
            setError({
              error: res.response.error,
              field: res.response.field,
            })
          } else {
            showFailure(res.response.error)
          }
        }
      },
    )
  }

  const AddNewAddress = () => {
    showPageLoader()
    if (isSameAddress) {
      setNewOrderData({
        ...newOrderData,
        billing: shipping,
        shipping: shipping,
        is_billing_address_same: true,
      })
      setDetails({
        ...details,
        is_editable: true,
        billing: shipping,
        shipping: shipping,
        is_billing_address_same: true,
      })
      setIsCustomerAdd(true)
      handleClose()
    } else {
      setNewOrderData({
        ...newOrderData,
        billing: billing,
        shipping: shipping,
        is_billing_address_same: false,
      })
      setDetails({
        ...newOrderData,
        billing: billing,
        shipping: shipping,
        is_editable: true,
        is_billing_address_same: false,
      })
      setIsCustomerAdd(true)
      handleClose()
    }
    hidePageLoader()
  }
  useEffect(() => {
    const billing_address = newOrder
      ? newOrderData.billing
      : OrderAddresses.billing_address
    const shipping_address = newOrder
      ? newOrderData.shipping
      : OrderAddresses.shipping_address
    setBilling({
      ...billing,
      address_id: billing_address?.address_id,
      address1: billing_address?.address1,
      city: billing_address?.city || '',
      country_name: billing_address?.country,
      country_code: billing_address?.country_code,
      email: billing_address?.email,
      first_name: billing_address?.first_name,
      last_name: billing_address?.last_name,
      phone: billing_address?.phone,
      postcode: billing_address?.postcode,
      state: billing_address?.state,
      state_name: billing_address?.city,
      state_code: billing_address?.state_code,
    })
    setShipping({
      ...shipping,
      address_id: shipping_address?.address_id || '',
      address1: shipping_address?.address1 || '',
      city: shipping_address?.city || '',
      country_name: shipping_address?.country || '',
      country_code: shipping_address?.country_code || '',
      email: shipping_address?.email || '',
      first_name: shipping_address?.first_name || '',
      last_name: shipping_address?.last_name || '',
      phone: shipping_address?.phone || '',
      postcode: shipping_address?.postcode || '',
      state: shipping_address?.state || '',
      state_code: shipping_address?.state_code || '',
      state_name: shipping_address?.city || '',
    })
  }, [OrderAddresses, newOrderData])

  const fetchShippingCountryData = (value) => {
    if (value) {
      const countryName = allowcountries?.find(
        (item) => item.two_letter_abbreviation === value,
      ).name
      setShipping({
        ...shipping,
        country_code: value,
        country_name: countryName,
      })
    }
  }
  const fetchBillingCountryData = (value) => {
    if (value) {
      const countryName = allowcountries?.find(
        (item) => item.two_letter_abbreviation === value,
      ).name
      setBilling({
        ...billing,
        country_code: value,
        country_name: countryName,
      })
    }
  }
  const CountriesList = countries.list
  return (
    <ClickOutside onClickOutside={() => handleClose(details)}>
      <SectionContent
        style={{ height: 'calc(100vh - 110px)', overflow: 'auto' }}
      >
        {/* <form onSubmit={handleSubmit}> */}
        <Title>Shipping Address</Title>
        <FormGroup>
          <DcDiv
            width="100%"
            height="auto"
            style={{
              display: 'flex',

              flexDirection: 'column',
              alignItems: 'flexStart',
            }}
          >
            <DcText
              style={{
                textAlign: 'left',

                color: '#2e314a',
                fontSize: '.875rem',
                fontWeight: '500',
                marginBottom: '.5rem',
              }}
            >
              Phone
            </DcText>
            <DcInput
              error={
                error.field === 'phone' && error.error !== ''
                  ? error.error
                  : null
              }
              required
              type={'text'}
              tabIndex={1}
              name={'phone'}
              placeholder={'Phone Number *'}
              value={shipping?.phone}
              onChange={(e) =>
                setShipping({ ...shipping, phone: e.target.value })
              }
            />
          </DcDiv>
          <DcDiv
            width="100%"
            height="auto"
            style={{
              display: 'flex',

              flexDirection: 'column',
              alignItems: 'flexStart',
            }}
          >
            <DcText
              style={{
                textAlign: 'left',

                color: '#2e314a',
                fontSize: '.875rem',
                fontWeight: '500',
                marginBottom: '.5rem',
              }}
            >
              Fist Name
            </DcText>
            <DcInput
              error={
                error.field === 'first_name' && error.error !== ''
                  ? error.error
                  : null
              }
              required
              tabIndex={2}
              type={'text'}
              name={'first_name'}
              placeholder={'First Name *'}
              value={shipping?.first_name}
              onChange={(e) =>
                setShipping({ ...shipping, first_name: e.target.value })
              }
            />
          </DcDiv>
          <DcDiv
            width="100%"
            height="auto"
            style={{
              display: 'flex',

              flexDirection: 'column',
              alignItems: 'flexStart',
            }}
          >
            <DcText
              style={{
                textAlign: 'left',

                color: '#2e314a',
                fontSize: '.875rem',
                fontWeight: '500',
                marginBottom: '.5rem',
              }}
            >
              Last Name
            </DcText>
            <DcInput
              error={
                error.field === 'last_name' && error.error !== ''
                  ? error.error
                  : null
              }
              type={'text'}
              tabIndex={3}
              name={'last_name'}
              placeholder={'Last Name'}
              value={shipping?.last_name}
              onChange={(e) =>
                setShipping({ ...shipping, last_name: e.target.value })
              }
            />
          </DcDiv>
        </FormGroup>
        <FormGroup>
          <DcDiv
            width="100%"
            height="auto"
            style={{
              display: 'flex',

              flexDirection: 'column',
              alignItems: 'flexStart',
            }}
          >
            <DcText
              style={{
                textAlign: 'left',

                color: '#2e314a',
                fontSize: '.875rem',
                fontWeight: '500',
                marginBottom: '.5rem',
              }}
            >
              Email
            </DcText>
            <DcInput
              error={
                error.field === 'email' && error.error !== ''
                  ? error.error
                  : null
              }
              type={'email'}
              tabIndex={4}
              name={'email'}
              placeholder={'Email'}
              value={shipping?.email}
              onChange={(e) =>
                setShipping({ ...shipping, email: e.target.value })
              }
            />
          </DcDiv>
          <DcDiv
            width="100%"
            height="auto"
            style={{
              display: 'flex',

              flexDirection: 'column',
              alignItems: 'flexStart',
            }}
          >
            <DcText
              style={{
                textAlign: 'left',

                color: '#2e314a',
                fontSize: '.875rem',
                fontWeight: '500',
                marginBottom: '.5rem',
              }}
            >
              City
            </DcText>
            <SelectCity
              cities={cities}
              value={
                shipping?.city?.toLowerCase() ||
                OrderAddresses?.shipping_address?.city ||
                newOrderData?.shipping?.city?.toLowerCase()
              }
              id={null}
              setData={setShipping}
              data={shipping}
              bulkEdit={false}
              placeholder="City *"
            />
          </DcDiv>
          <DcDiv
            width="100%"
            height="auto"
            style={{
              display: 'flex',

              flexDirection: 'column',
              alignItems: 'flexStart',
            }}
          >
            <DcText
              style={{
                textAlign: 'left',

                color: '#2e314a',
                fontSize: '.875rem',
                fontWeight: '500',
                marginBottom: '.5rem',
              }}
            >
              Country
            </DcText>
            <DcInput
              value="Pakistan"
              disabled={true}
              placeholder="Pakistan"
              name="country"
              style={{
                backgroundColor: '#f5f5f5',
                cursor: 'not-allowed',
              }}
            />
          </DcDiv>
        </FormGroup>
        <FormGroup>
          <DcDiv
            width="100%"
            height="auto"
            style={{
              display: 'flex',

              flexDirection: 'column',
              alignItems: 'flexStart',
            }}
          >
            <DcText
              style={{
                textAlign: 'left',

                color: '#2e314a',
                fontSize: '.875rem',
                fontWeight: '500',
                marginBottom: '.5rem',
              }}
            >
              Post Code
            </DcText>

            <DcInput
              error={
                error.field === 'shipping_address__postcode' &&
                error.error !== ''
                  ? error.error
                  : null
              }
              type={'text'}
              name={'last_name'}
              placeholder="Postcode"
              tabIndex={7}
              value={shipping?.postcode}
              onChange={(e) => {
                setShipping({ ...shipping, postcode: e.target.value })
              }}
            />
          </DcDiv>
        </FormGroup>
        <FormGroup>
          <DcDiv
            width="100%"
            height="auto"
            style={{
              display: 'flex',

              flexDirection: 'column',
              alignItems: 'flexStart',
            }}
          >
            <DcText
              style={{
                textAlign: 'left',

                color: '#2e314a',
                fontSize: '.875rem',
                fontWeight: '500',
                marginBottom: '.5rem',
              }}
            >
              Address
            </DcText>
            <DcInput
              error={
                error.field === 'address1' && error.error !== ''
                  ? error.error
                  : null
              }
              required
              as={'textarea'}
              name={'address1'}
              tabIndex={4}
              placeholder={'Address *'}
              value={shipping?.address1}
              onChange={(e) =>
                setShipping({ ...shipping, address1: e.target.value })
              }
            />
          </DcDiv>
        </FormGroup>
        &nbsp;
        <FormGroup>
          <DcFlexBox width="100%" gap="10px">
            <Title>Billing Information : Same as Shipping Address</Title>
            <DcSwitch
              onChange={(e) => {
                setIsSameAddress(e.target.checked)
              }}
              checked={isSameAddress}
              label={isSameAddress ? 'Yes' : 'No'}
            />
          </DcFlexBox>
        </FormGroup>
        &nbsp;
        {isSameAddress ? (
          ''
        ) : (
          <>
            <FormGroup>
              <DcDiv
                width="100%"
                height="auto"
                style={{
                  display: 'flex',

                  flexDirection: 'column',
                  alignItems: 'flexStart',
                }}
              >
                <DcText
                  style={{
                    textAlign: 'left',

                    color: '#2e314a',
                    fontSize: '.875rem',
                    fontWeight: '500',
                    marginBottom: '.5rem',
                  }}
                >
                  Phone
                </DcText>
                <DcInput
                  error={
                    error.field === 'phone' && error.error !== ''
                      ? error.error
                      : null
                  }
                  required
                  type={'text'}
                  name={'phone'}
                  placeholder={'Phone Number *'}
                  value={billing?.phone}
                  onChange={(e) =>
                    setBilling({ ...billing, phone: e.target.value })
                  }
                />
              </DcDiv>
              <DcDiv
                width="100%"
                height="auto"
                style={{
                  display: 'flex',

                  flexDirection: 'column',
                  alignItems: 'flexStart',
                }}
              >
                <DcText
                  style={{
                    textAlign: 'left',

                    color: '#2e314a',
                    fontSize: '.875rem',
                    fontWeight: '500',
                    marginBottom: '.5rem',
                  }}
                >
                  First Name
                </DcText>
                <DcInput
                  error={
                    error.field === 'first_name' && error.error !== ''
                      ? error.error
                      : null
                  }
                  required
                  type={'text'}
                  name={'first_name'}
                  placeholder={'First Name *'}
                  value={billing?.first_name}
                  onChange={(e) =>
                    setBilling({ ...billing, first_name: e.target.value })
                  }
                />
              </DcDiv>
              <DcDiv
                width="100%"
                height="auto"
                style={{
                  display: 'flex',

                  flexDirection: 'column',
                  alignItems: 'flexStart',
                }}
              >
                <DcText
                  style={{
                    textAlign: 'left',

                    color: '#2e314a',
                    fontSize: '.875rem',
                    fontWeight: '500',
                    marginBottom: '.5rem',
                  }}
                >
                  Last Name
                </DcText>
                <DcInput
                  error={
                    error.field === 'last_name' && error.error !== ''
                      ? error.error
                      : null
                  }
                  type={'text'}
                  name={'last_name'}
                  placeholder={'Last Name *'}
                  value={billing?.last_name}
                  onChange={(e) =>
                    setBilling({ ...billing, last_name: e.target.value })
                  }
                />
              </DcDiv>
            </FormGroup>
            <FormGroup>
              <DcDiv
                width="100%"
                height="auto"
                style={{
                  display: 'flex',

                  flexDirection: 'column',
                  alignItems: 'flexStart',
                }}
              >
                <DcText
                  style={{
                    textAlign: 'left',

                    color: '#2e314a',
                    fontSize: '.875rem',
                    fontWeight: '500',
                    marginBottom: '.5rem',
                  }}
                >
                  Email
                </DcText>
                <DcInput
                  error={
                    error.field === 'email' && error.error !== ''
                      ? error.error
                      : null
                  }
                  type={'email'}
                  name={'email'}
                  placeholder={'Email *'}
                  value={billing?.email}
                  onChange={(e) =>
                    setBilling({ ...billing, email: e.target.value })
                  }
                />
              </DcDiv>

              <DcDiv
                width="100%"
                height="auto"
                style={{
                  display: 'flex',

                  flexDirection: 'column',
                  alignItems: 'flexStart',
                }}
              >
                <DcText
                  style={{
                    textAlign: 'left',

                    color: '#2e314a',
                    fontSize: '.875rem',
                    fontWeight: '500',
                    marginBottom: '.5rem',
                  }}
                >
                  City
                </DcText>

                <SelectCity
                  cities={cities}
                  value={
                    billingAddress?.city?.toLowerCase() ||
                    OrderAddresses?.billing_address?.city ||
                    newOrderData?.billing?.city?.toLowerCase()
                  }
                  id={null}
                  setData={setBilling}
                  data={billing}
                  bulkEdit={false}
                  placeholder="City *"
                />
              </DcDiv>
              <DcDiv
                width="100%"
                height="auto"
                style={{
                  display: 'flex',

                  flexDirection: 'column',
                  alignItems: 'flexStart',
                }}
              >
                <DcText
                  style={{
                    textAlign: 'left',

                    color: '#2e314a',
                    fontSize: '.875rem',
                    fontWeight: '500',
                    marginBottom: '.5rem',
                  }}
                >
                  Country
                </DcText>
                <DcInput
                  value="Pakistan"
                  disabled={true}
                  placeholder="Pakistan"
                  name="country"
                  style={{
                    backgroundColor: '#f5f5f5',
                    cursor: 'not-allowed',
                  }}
                />
              </DcDiv>
            </FormGroup>

            <FormGroup>
              <DcDiv
                width="100%"
                height="auto"
                style={{
                  display: 'flex',

                  flexDirection: 'column',
                  alignItems: 'flexStart',
                }}
              >
                <DcText
                  style={{
                    textAlign: 'left',

                    color: '#2e314a',
                    fontSize: '.875rem',
                    fontWeight: '500',
                    marginBottom: '.5rem',
                  }}
                >
                  Post Code
                </DcText>
                <DcInput
                  error={
                    error.field === 'billing_address__postcode' &&
                    error.error !== ''
                      ? error.error
                      : null
                  }
                  type={'text'}
                  name={'postcode'}
                  placeholder="Postcode *"
                  value={billing?.postcode}
                  onChange={(e) =>
                    setBilling({ ...billing, postcode: e.target.value })
                  }
                />
              </DcDiv>
            </FormGroup>

            <FormGroup>
              <DcDiv
                width="100%"
                height="auto"
                style={{
                  display: 'flex',

                  flexDirection: 'column',
                  alignItems: 'flexStart',
                }}
              >
                <DcText
                  style={{
                    textAlign: 'left',

                    color: '#2e314a',
                    fontSize: '.875rem',
                    fontWeight: '500',
                    marginBottom: '.5rem',
                  }}
                >
                  Address
                </DcText>
                <DcInput
                  error={
                    error.field === 'address1' && error.error !== ''
                      ? error.error
                      : null
                  }
                  required
                  as={'textarea'}
                  name={'address1'}
                  placeholder={'  Address *'}
                  value={billing?.address1}
                  onChange={(e) =>
                    setBilling({ ...billing, address1: e.target.value })
                  }
                />
              </DcDiv>
            </FormGroup>
          </>
        )}
        <FormGroup style={{ padding: '5px 20px 0 0' }} flexEnd>
          <DcButton
            size="sm"
            variant="outline-secondary"
            // onClick={() => {
            //   handleClose()
            // }}
          >
            Close
          </DcButton>
          <DcButton
            onClick={() => handleSubmit()}
            size="sm"
            variant="primary"
            type="button"
          >
            Save
          </DcButton>
        </FormGroup>
        {/* </form> */}
      </SectionContent>
    </ClickOutside>
  )
}
const Title = styled.div`
  color: rgba(0, 0, 0, 0.87);
  font-size: 16px;
  font-weight: 400;
  align-self: inherit;
  width: 100%;
`

const FormGroup = styled.div<{ flexEnd?: boolean }>`
  display: inline-flex;
  flex-direction: row;
  flex-wrap: nowrap;
  justify-content: ${(props) => (props.flexEnd ? 'flex-end' : 'flex-start')};
  align-items: flex-start;
  align-content: flex-start;
  padding-bottom: 10px;
  width: 100%;
  flex-grow: 1;
  gap: 10px;
  .full-width {
    flex-grow: 1;
  }
`

export default EditCustomerInfo
